import { useState, useCallback, useEffect } from "react";
import { ReportGenReport } from "../../../shared/interfaces/ReportGenReport";
import rmsClient from "../../../api/clients/rmsClient";
import { SortConfig } from "../types";
import { useJobStore } from "../../../store/jobStore";
import { ReportJobStatus } from "../../../shared/interfaces/JobStatus";
import { useReportFilters } from "./useReportFilters";

export const useReportsData = (
  filters: ReturnType<typeof useReportFilters>,
  page: number,
  showError: (message: string) => void,
  showSuccess: (message: string) => void
) => {
  const [isLoading, setIsLoading] = useState(false);
  const [reports, setReports] = useState<ReportGenReport[]>([]);
  const [totalReports, setTotalReports] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState<SortConfig | null>({
    key: "reportedByDate",
    direction: "desc",
  });
  
  // Delete modal state
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [reportToDelete, setReportToDelete] = useState<ReportGenReport | null>(null);

  const jobs = useJobStore((state) => state.jobs);
  const pendingJobs = jobs.filter(
    (job) =>
      job.status === ReportJobStatus.PENDING ||
      job.status === ReportJobStatus.AWAITING_INPUT
  );

  const fetchReports = useCallback(() => {
    setIsLoading(true);
    rmsClient
      .get("/reports", {
        params: {
          page,
          limit: rowsPerPage,
          sortBy: sortConfig?.key || "reportedByDate",
          sortOrder: sortConfig?.direction || "desc",
          status:
            filters.selectedStatuses.length > 0
              ? filters.selectedStatuses.join(",")
              : undefined,
          dateFrom: filters.dateFrom?.toISOString(),
          dateTo: filters.dateTo?.toISOString(),
          reportedDateFrom: filters.reportedDateFrom?.toISOString(),
          reportedDateTo: filters.reportedDateTo?.toISOString(),
          details: filters.debouncedDetailsFilter || undefined,
          reportedBy:
            filters.reportedByFilter.length > 0
              ? filters.reportedByFilter.map((r) => r.id).join(",")
              : undefined,
          modifiedBy:
            filters.modifiedByFilter.length > 0
              ? filters.modifiedByFilter.map((m) => m.id).join(",")
              : undefined,
        },
      })
      .then((res) => {
        const reportsFetched = res.data.reports || [];
        setReports(reportsFetched);
        setTotalReports(res.data.total ?? reportsFetched.length);
      })
      .catch((err) => {
        const errorMessage =
          err.response?.data?.error ||
          "An error occurred while fetching reports. Please try again.";
        showError(errorMessage);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [
    page,
    rowsPerPage,
    sortConfig,
    filters.selectedStatuses,
    filters.dateFrom,
    filters.dateTo,
    filters.reportedDateFrom,
    filters.reportedDateTo,
    filters.debouncedDetailsFilter,
    filters.reportedByFilter,
    filters.modifiedByFilter,
    showError,
  ]);

  const handleReportDelete = () => {
    setOpenDeleteModal(false);
    rmsClient
      .delete(`/report`, {
        params: { reportId: reportToDelete?.id },
      })
      .then(() => {
        showSuccess(`${reportToDelete?.reportName} successfully deleted.`);
        fetchReports();
      })
      .catch((err) => {
        const errorMessage =
          err.response.data.error ||
          `Failed to delete ${reportToDelete?.reportName}. Please try again later.`;
        showError(errorMessage);
        console.error(err);
      });
  };

  const handleModalClose = () => {
    setOpenDeleteModal(false);
  };

  const handleOpenDeleteModal = (report: ReportGenReport) => {
    setReportToDelete(report);
    setOpenDeleteModal(true);
  };

  const requestSort = (key: string) => {
    let direction: "asc" | "desc" = "asc";

    if (sortConfig && sortConfig.key === key) {
      direction = sortConfig.direction === "asc" ? "desc" : "asc";
    }

    setSortConfig({ key, direction });
  };

    // Fetch reports when pendingJobs list changes
  useEffect(() => {
      fetchReports();
  }, [pendingJobs.length, fetchReports]);


  return {
    reports,
    totalReports,
    isLoading,
    setIsLoading,
    rowsPerPage,
    setRowsPerPage,
    sortConfig,
    openDeleteModal,
    reportToDelete,
    handleReportDelete,
    handleModalClose,
    handleOpenDeleteModal,
    requestSort,
    fetchReports
  };
};
